import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  const category = useMemo(() => categories.find(c => c.slug === slug), [slug]);
  const comics = useMemo(() => {
    if (!slug) return [];
    const raw = getComicsByCategory(slug);
    if (sortBy === 'popular') return [...raw].sort((a, b) => b.views - a.views);
    if (sortBy === 'newest') return [...raw].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return [...raw].sort((a, b) => b.rating - a.rating);
  }, [slug, sortBy]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画分类不存在或已被移除</p>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - 非麻瓜漫画 | 免费下拉式${category.name}全集`}
        description={`非麻瓜漫画为您提供最全${category.name}免费在线阅读。${category.description}下拉式一看到底，畅享精彩漫画。`}
        keywords={`${category.name},非麻瓜漫画,免费漫画,下拉式漫画,在线漫画,魔法世界漫画`}
        canonicalUrl={`https://anhuicloud.cn/category/${category.slug}`}
      />
      <SchemaOrg data={{
        '@type': 'CollectionPage',
        name: `${category.name} - 非麻瓜漫画`,
        description: category.description,
        url: `https://anhuicloud.cn/category/${category.slug}`,
      }} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { name: '首页', href: '/' },
                  { name: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl">
                <i className={`${category.icon} text-background-50 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span>共 <strong className="text-foreground-700">{comics.length}</strong> 部作品</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500">排序：</span>
              <div className="flex items-center bg-background-100 rounded-lg p-1">
                {([
                  { key: 'popular' as const, label: '人气', icon: 'ri-fire-line' },
                  { key: 'newest' as const, label: '最新', icon: 'ri-timer-line' },
                  { key: 'rating' as const, label: '评分', icon: 'ri-star-line' },
                ]).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${opt.icon} mr-1`}></i>{opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {comics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-book-open-line text-4xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画作品</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
              {comics.map((comic) => (
                <article
                  key={comic.id}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/comic/${comic.slug}`)}
                >
                  <div className="relative overflow-hidden rounded-xl mb-3 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - ${category.name} - 非麻瓜漫画`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        comic.status === '已完结'
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-accent-500 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <div className="flex items-center gap-2 text-background-50 text-xs">
                        <i className="ri-star-fill text-primary-400"></i>
                        <span>{comic.rating}</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}